import React, { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { useTranslate } from '@tolgee/react';
import Select from 'react-select'
import LocalStorage from '../../Helpers/LocalStorage'
import { useGenericComponentsStateService } from '../../Context/GenericComponentsStateContext'

const CallAudioSettings = ({
    audioInputDevice,
    audioOutputDevice,
    setSelectedAudioInput,
    setSelectedAudioOutput,
    micAllowed = true,
    remoteNoiseFilterVisible = false,
    remoteNoiseFilterEnabled = false,
    setRemoteNoiseFilterEnabled,
}
) => {
    const { selectedMenuAudioInput, setSelectedMenuAudioInput, selectedMenuAudioOutput, setSelectedMenuAudioOutput } = useGenericComponentsStateService();

    const theme = useTheme();

    const selectedCustomStyles = {
        container: (provided) => ({
            ...provided,
            "&:focus-visible": {
                outline: "none",
            },

            fontSize: "14px",
            color: theme.menu.primaryTextColor,

        }),
        control: (provided) => ({
            ...provided,
            "&:focus-visible": {
                outline: "none",
            },
            "&:selected": {
                outline: "none",
            },
            "&:hover": {
                boxShadow: "none",
            },

            boxShadow: "none",
            borderColor: theme.menu.groupContentBackground,
            background: theme.menu.groupContentBackground,
            color: theme.menu.primaryTextColor,
            outline: "none"
        }),
        menuList: (provided) => ({
            ...provided,
            background: theme.menu.groupContentBackground,
            color: theme.menu.primaryTextColor,
        }),
        option: (provided, { isSelected }) => ({
            ...provided,
            "&:hover": {
                background: theme.menu.enterPhoneNumberBackground,
                color: theme.menu.selectedTextColor
            },
            "&:active": {
                background: theme.menu.groupContentBackground
            },
            "&:selected": {
                background: theme.menu.primaryTextColor
            },
            background: isSelected ? theme.menu.selectedTextColor : "inherit"

        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: "14px",
            fontWeight: "500",
            color: theme.menu.primaryTextColor,
        }),

    }

    const filterDefault = (devices) => {
        let filteredDevices = devices.filter((item) => item.deviceId !== "default")
        return filteredDevices
    }
    const handleSyncSelectedInput = (deviceId) => {
        let found = audioInputDevice.devices.find((item) => item.deviceId === deviceId)
        setSelectedMenuAudioInput(found)
    }

    const handleSyncSelectedOutput = (deviceId) => {
        let found = audioOutputDevice.devices.find((item) => item.deviceId === deviceId)
        setSelectedMenuAudioOutput(found)
        if (audioOutputDevice.activeDeviceId === "") {
            let defaultDevice = audioOutputDevice.devices.filter((item) => item.deviceId !== "default")
            if (defaultDevice && defaultDevice.length > 0) {
                audioOutputDevice.setActiveMediaDevice(defaultDevice[0].deviceId)
            }
        }
    }

    const handleChangeInputDevice = (deviceId) => {
        audioInputDevice.setActiveMediaDevice(deviceId)
        LocalStorage.setInputDevice(deviceId)
        setSelectedAudioInput(deviceId)
    }

    const handleChangeOutputDevice = (deviceId) => {
        audioOutputDevice.setActiveMediaDevice(deviceId)
        LocalStorage.setOutputDevice(deviceId)
        setSelectedAudioOutput(deviceId)
    }

    useEffect(() => {
        handleSyncSelectedInput(audioInputDevice.activeDeviceId)
    }, [audioInputDevice])

    useEffect(() => {
        handleSyncSelectedOutput(audioOutputDevice.activeDeviceId)
    }, [audioOutputDevice])

    const { t } = useTranslate();
    return (
        <Container>
            {micAllowed && (
                <Group>
                    <GroupSpan>
                        {t('general.microphone')}
                    </GroupSpan>
                    <Select
                        value={selectedMenuAudioInput}
                        onChange={(e) => handleChangeInputDevice(e.deviceId)}
                        options={filterDefault(audioInputDevice.devices)}
                        isSearchable={false}
                        isOptionSelected={(e) => audioInputDevice.activeDeviceId === e.deviceId}
                        styles={selectedCustomStyles}
                    >
                    </Select>
                </Group>
            )}
            {audioOutputDevice.devices.length > 0 &&
                <Group>
                    <GroupSpan>
                        {t('callOptionModal.audio_output')}
                    </GroupSpan>
                    <Select
                        value={selectedMenuAudioOutput}
                        onChange={(e) => handleChangeOutputDevice(e.deviceId)}
                        options={filterDefault(audioOutputDevice.devices)}
                        isSearchable={false}
                        isOptionSelected={(e) => audioOutputDevice.activeDeviceId === e.deviceId}
                        styles={selectedCustomStyles}
                    >
                    </Select>
                </Group>
            }
            {remoteNoiseFilterVisible && (
                <Group>
                    <ToggleRow onClick={() => setRemoteNoiseFilterEnabled?.(!remoteNoiseFilterEnabled)}>
                        <div>
                            <GroupSpan>
                                Reduce background noise from others
                            </GroupSpan>
                        </div>
                        <ToggleSwitch $enabled={remoteNoiseFilterEnabled}>
                            <ToggleKnob $enabled={remoteNoiseFilterEnabled} />
                        </ToggleSwitch>
                    </ToggleRow>
                </Group>
            )}
        </Container>
    )
}

export default CallAudioSettings
const Container = styled.div`
display:flex;
flex-direction:column;
gap:20px;
padding:0 20px;
`
const Group = styled.div`
display:flex;
flex-direction:column;
gap:10px;
`
const ToggleRow = styled.div`
display:flex;
align-items:center;
justify-content:space-between;
gap:10px;
cursor:pointer;
`
const ToggleSwitch = styled.div`
  width: 40px;
  height: 22px;
  background: ${({ $enabled, theme }) => $enabled ? "#10b981" : theme.callComponent.translationModeBg};
  border-radius: 11px;
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;
`
const ToggleKnob = styled.div`
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: ${({ $enabled }) => ($enabled ? "20px" : "2px")};
  transition: left 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`
const Line = styled.div`
display:flex;
gap:10px;
`
const GroupSpan = styled.p`
font-size:16px;
line-height:18px;
font-weight:500;
color:${({ theme }) => theme.menu.secondaryTextColor};
`
